import { ChevronDown, ChevronRight, Copy, MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useExactI18nLabel } from "~/common/functions/useDisplayLocale";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "~/components/ui";
import type { CategoryHierarchyNode } from "~/features/admin/categories/types";
import { cn } from "~/lib/utils";

interface CategoryHierarchyTableProps {
  rows: CategoryHierarchyNode[];
  categoryType: "entity" | "relation";
  onSelect: (id: string) => void;
  emptyMessage: string;
}

/**
 * Hierarchical (expandable) category table. Mirrors V1's `CategoriesTable`
 * (the model-admin variant) — renders rows + collapsible children by
 * `categoryPath`. Built on plain HTML + shadcn primitives, no TanStack Table.
 */
export function CategoryHierarchyTable({ rows, categoryType, onSelect, emptyMessage }: CategoryHierarchyTableProps) {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
      <table className="w-full min-w-max border-collapse">
        <thead>
          <tr className="bg-bg-subtle">
            <Th className="w-12" />
            <Th>{t("model.common.name")}</Th>
            <Th>{t("feature.categories.table.label")}</Th>
            <Th>{t("feature.categories.table.description")}</Th>
            <Th>{t("feature.categories.table.example")}</Th>
            <Th className="text-right" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-3.5 py-10 text-center text-14-regular text-fg-3">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => <Row key={row.id} row={row} depth={0} categoryType={categoryType} onSelect={onSelect} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

interface RowProps {
  row: CategoryHierarchyNode;
  depth: number;
  categoryType: "entity" | "relation";
  onSelect: (id: string) => void;
}

function Row({ row, depth, categoryType, onSelect }: RowProps) {
  const { t } = useTranslation();
  const exactLabel = useExactI18nLabel();
  const hasChildren = !!row.children && row.children.length > 0;
  // V1 default: only "ctn" at the entity root expands by default.
  const [open, setOpen] = useState(row.categoryPath?.path === "ctn" && categoryType === "entity");
  // Label cell: strict per-locale read — empty when the row has no value for the current locale.
  const localeLabel = exactLabel(row.i18nLabel as Record<string, unknown> | null);
  const exampleName = row.recordMetadataCategories[0]?.recordMetadata.record?.name ?? "";

  const handleCopyId = async () => {
    try {
      await copyToClipboard(row.id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  return (
    <>
      <tr className="border-b border-border-default last:border-b-0 hover:bg-primary-50">
        <Td className="w-12">
          {hasChildren ? (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-label={open ? t("ui.hide") : t("ui.show")}
              className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-xs)] text-fg-2 hover:bg-bg-subtle hover:text-fg-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            >
              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          ) : null}
        </Td>
        <Td className="font-medium text-fg-1">
          <span style={{ paddingLeft: depth * 16 }} className="inline-flex items-center">
            {row.name}
          </span>
        </Td>
        <Td>{localeLabel ?? ""}</Td>
        <Td>
          <div className="max-w-[360px] whitespace-normal break-words text-fg-2">{row.description || ""}</div>
        </Td>
        <Td>
          <div className="max-w-[220px] truncate" title={exampleName}>
            {exampleName}
          </div>
        </Td>
        <Td className="text-right">
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={handleCopyId}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Td>
      </tr>
      {hasChildren &&
        open &&
        row.children?.map((child) => (
          <Row key={child.id} row={child} depth={depth + 1} categoryType={categoryType} onSelect={onSelect} />
        ))}
    </>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "border-b border-border-default px-3.5 py-2.5 text-left text-10-regular uppercase tracking-[0.06em] text-fg-2",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn("px-3.5 py-2.5 text-14-regular align-middle", className)}>{children}</td>;
}
